import { AllFilterTypeConditions } from '@nirvana/shared/dtos/types/shared/filters';
import { Status, Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { Feature } from '@nirvana/shared/constants';

export interface GetUserConfigForDashboardPayload {
  dashboardType: DashboardType;
  lng: string;
}

export interface GetUserConfigForDashboardRequest {
  payload: GetUserConfigForDashboardPayload;
}

export interface CanExportConfig {
  csv: boolean;
  pdf: boolean;
  report: boolean;
}

interface LiveViewConfig {
  visible: boolean;
  usable: boolean;
}

export interface SettingsPanelConfig {
  visible: boolean;
  hasSearch: boolean;
  searchInputPlaceholder?: string;
  searchInputNoResultsFoundText?: string;
}

export interface SmartViewsConfig {
  visible: boolean;
  hasSearch: boolean;
  title: string;
  isOpen: boolean;
  searchInputPlaceholder?: string;
  searchInputNoResultsFoundText?: string;
}

export interface PopUpConfiguration {
  shouldDisplayDiscard: boolean;
  shouldDisplayUnsavedChanges: boolean;
}

export enum FeedbackType {
  HEALTH_DASHBOARD = 3,
  ASM_DASHBOARD = 1,
}

export interface DashboardUiConfig {
  canExport: CanExportConfig;
  liveView: LiveViewConfig;
  settingsPanel: SettingsPanelConfig;
  smartViewsPanel: SmartViewsConfig;
  dashboardType: DashboardType;
  popUp: PopUpConfiguration;
  resetView?: boolean;
  feedbackType?: FeedbackType;
  isEarlyAccess?: boolean;
  showFeedbackFormBtn?: boolean;
}

export enum DashboardType {
  HEALTH_DASHBOARD = 1,
  RISK_MANAGEMENT = 2,
  ASM = 3,
  EXECUTIVE_SUMMARY = 4,
}

export const DashboardTypeArr = Object.values(DashboardType);

export const DashboardTypeToStringMap = new Map<DashboardType, string>([
  [DashboardType.HEALTH_DASHBOARD, 'healthDashboard'],
  [DashboardType.EXECUTIVE_SUMMARY, 'executiveSummary'],
  [DashboardType.RISK_MANAGEMENT, 'riskManagement'],
  [DashboardType.ASM, 'asmDashboard'],
]);

export enum CategoryGroup {
  DEFAULT_TOP = 0,
  DEFAULT_SECOND = 1,
  SAVED = 2,
}

export enum WidgetCategory {
  CATEGORY_ZERO = 0,
  CATEGORY_ONE = 1,
  CATEGORY_EASM = 2,
  CATEGORY_PHASR = 3,
  CATEGORY_RISK_MANAGEMENT = 4,
}

export enum ViewType {
  DEFAULT = 0,
  CUSTOM = 1,
}

export interface Position {
  x?: number;
  y?: number;
}

export interface Size {
  width?: number;
  height?: number;
}

export interface Layout {
  position: Position;
  size: Size;
}

export enum ContentOrientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
  VERTICAL_FLEX = 'vertical-flex',
}

export interface WidgetConfig {
  defaultSize: Size;
  maxSize?: Size;
  minSize?: Size;
  resizeBreakingPoints?: number[];
  resizable: boolean;
  movable: boolean;
  closable: boolean;
  locked: boolean;
  hideTitle?: boolean;
  contentOrientation?: ContentOrientation;
  widgetContentStyle?: string;
  forbidden?: boolean;
  titleHasAction?: boolean;
  alignTitle?: String;
}

export interface WidgetCategoryConfig {
  id: string;
  title: string;
  isOpen: boolean;
  category: WidgetCategory;
}

export interface SmartViewWidget {
  id: string;
  defaultLayout: Layout;
}

export interface Widget {
  id: string; // needs to be widget tag name like 'gz-health-dashboard-overview'
  category: WidgetCategory;
  config: WidgetConfig;
}

export interface GenericViewConfig {
  id: string;
  label: string;
  categoryGroup: CategoryGroup;
  type: ViewType;
  filters: Filter[];
  widgets: SmartViewWidget[];
  default?: boolean;
  availableForFeatures?: Feature[];
}

export interface ActiveViewProps {
  id: string;
  type: ViewType;
}

export enum FilterKeys {
  COMPANY = 'company',
  DATE_TIME = 'dateTime',
  DETECTING_MODULE = 'detectingModule',
  SHA_256 = 'sha256',
  HOST_NAME = 'hostname',
  IP = 'ip',
  TAGS = 'tags',
  LAST_SEEN = 'lastSeen',
}

export type Filter = {
  [filterKey in FilterKeys]?: AllFilterTypeConditions;
};

export enum FilterType {
  DATE = 0,
  ENUM = 1,
  STRING = 2,
  NUMBER = 3,
  COMPANY = 6,
  SELECT = 7,
  RANGE = 8,
  TAGS = 11,
}

export enum InputWidthValues {
  SMALL = '80',
  MEDIUM = '160',
  LARGE = '240',
  XLARGE = '320',
  XXLARGE = '400',
  FULL = 'full',
}

export interface DateFilterUiConfig {
  // active range from today (nr of days)
  activeRange: number | null;
  enableSelectAllToDate?: boolean;
}

export interface NumberFilterUiConfig {
  inputWidth?: InputWidthValues;
}

export type AnyFilterUiConfig = DateFilterUiConfig | NumberFilterUiConfig;

export interface GridFilterUiConfiguration {
  config?: AnyFilterUiConfig;
}

export interface FilterValue {
  label: string;
  value: number | string;
}

export interface FilterConfig {
  key: FilterKeys;
  name: string;
  type: FilterType;
  values?: Array<FilterValue>;
  removable: boolean;
  isDefault?: boolean;
  enumFilterCanSelectAll?: boolean;
  ui?: GridFilterUiConfiguration;
  apiType?: string;
  useActiveCompany?: boolean;
}

export interface SmartViewCategoryConfig {
  id: string;
  label: string;
  isOpen: boolean;
  group: CategoryGroup;
  collapsable: boolean;
  emptyCategoryText: string;
}

export interface GetUserConfigForDashboardResponse {
  dashboardType: DashboardType;
  activeViewProps: ActiveViewProps;
  uiConfig: DashboardUiConfig;
  smartViews: GenericViewConfig[];
  smartViewsCategories: SmartViewCategoryConfig[];
  filterConfigs: FilterConfig[];
  widgetsCategories: WidgetCategoryConfig[];
  availableWidgets: Widget[];
}

export interface InitGenericDashboardResponse {
  data?: GetUserConfigForDashboardResponse;
  status: Status;
}

export interface CreateViewConfiguration {
  label: string;
  categoryGroup: CategoryGroup;
  type: ViewType;
  filters: Filter[];
  widgets: SmartViewWidget[];
}

export interface CreateViewRequest {
  dashboardType: DashboardType;
  viewConfig: CreateViewConfiguration;
}

export interface CreateViewResponse {
  viewConfig: GenericViewConfig;
}

export interface UpdateViewRequest {
  dashboardType: DashboardType;
  viewConfig: GenericViewConfig;
}

export interface UpdateViewResponse {
  viewConfig: GenericViewConfig;
}

export interface DeleteViewRequest {
  dashboardType: DashboardType;
  viewId: string;
}

export interface DeleteViewResponse {}

export interface ChangeActiveViewRequest {
  dashboardType: DashboardType;
  viewProps: ActiveViewProps;
}

export interface ChangeActiveViewResponse {
  viewConfig: GenericViewConfig;
}

export interface ValidateViewNamePayload {
  dashboardType: DashboardType;
  viewName: string;
  viewId: string;
}

export interface ValidateViewNameRequest {
  payload: ValidateViewNamePayload;
}

export interface ValidateViewNameResponse {
  valid: boolean;
}

export interface SetSmartViewsPanelStatusRequest {
  dashboardType: DashboardType;
  isSmartViewsPanelOpen: boolean;
}

export interface SetSmartViewsPanelStatusResponse {}

export interface SetWidgetCategoryStatusPayload {
  dashboardType: DashboardType;
  categoryId: string;
  isWidgetCategoryOpen: boolean;
}

export interface SetWidgetCategoryStatusRequest {
  payload: SetWidgetCategoryStatusPayload;
}

export interface SetWidgetCategoryStatusResponse {}

export interface SetSmartViewCategoryStatusRequest {
  dashboardType: DashboardType;
  categoryId: string;
  isSmartViewCategoryOpen: boolean;
}

export interface SetSmartViewCategoryStatusResponse {}

export interface UpdateDashboardConfigProps {
  shouldDisplayDiscard?: boolean;
  shouldDisplayUnsavedChanges?: boolean;
}
export interface UpdateDashboardConfigForUserRequest {
  dashboardType: DashboardType;
  changedProps: UpdateDashboardConfigProps;
}

export interface UpdateDashboardConfigForUserResponse {}

export enum CanExportServiceConfig {
  HIDDEN = 0,
  VISIBLE = 1,
}

export interface WidgetsPayloadMapData {
  widgetId: string;
  payload: string;
  state: number;
}

// taken from apps/webcomponents/src/global.d.ts
// only the necessary parts are included
interface UserData {
  profile: {
    timezone: string;
    fullName?: string;
    timezoneOffset?: string;
  };
  company: {
    id: string;
    name: string;
  };
}

export interface GlobalThisApplication {
  Main: {
    getLanguage: string;
    getUser: UserData;
    company: {
      isPartner: boolean;
    };
    hasAnyRights: boolean;
  };
}

export interface PageFormat {
  width: number;
  height: number;
  isLandscape: boolean;
}

export interface GetDashboardPdfRequest {
  filters: Array<Filter>;
  widgetsPayload: Array<WidgetsPayloadMapData>;
  userConfig: GetUserConfigForDashboardResponse;
  componentLoadedEventName: string;
  globalThisApplication: GlobalThisApplication;
  selectedTarget: Target;
  locale: string;
  timezone: string;
  pageFormat?: PageFormat;
}

export interface GetDashboardPdfResponse {
  pdfData: Buffer;
  pdfNamePrefix: string;
}
